import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Download, BarChart3, Search, FileText } from 'lucide-react';
import { securitfeeAPI, formatCurrency } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { buildCsvRaw, downloadCsv, downloadBlob } from '../utils/csvExport';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const ReportSecurityFee = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byOperator, setByOperator] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [exportingList, setExportingList] = useState(null);

  const applyFilters = () => {
    setAppliedMonth(month);
    setAppliedYear(year);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when applied filters change
  }, [appliedMonth, appliedYear, operatorFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { month: appliedMonth, year: appliedYear };

      const [, byOpRes] = await Promise.all([
        securitfeeAPI.getOverview(params),
        securitfeeAPI.getByOperator(params)
      ]);

      const rawByOp = Array.isArray(byOpRes) ? byOpRes : (byOpRes?.data || []);
      const fullList = rawByOp.map((r) => ({
        operator: r.operatorName || r.operator_name || r.operator,
        totalSubscribers: r.totalSubscribers || r.total_subscribers || 0,
        eligible: Number(r.eligibleSubscribers ?? r.eligible ?? r.eligible_subscribers ?? r.eligible_subscriber_count ?? 0) || 0,
        collected: Number(r.collectedAmount ?? r.collected ?? r.collected_amount ?? 0) || 0,
        remaining: Number(r.remainingAmount ?? r.remaining ?? r.remaining_amount ?? 0) || 0
      }));
      setOperatorOptions(fullList.map((r) => r.operator).filter(Boolean));
      const list = operatorFilter === 'all' ? fullList : fullList.filter((x) => String(x.operator) === String(operatorFilter));
      setByOperator(list);
    } catch (err) {
      console.error(err);
      setError(err.message || t('reportSecurityFee.loadError'));
      setByOperator([]);
    } finally {
      setLoading(false);
    }
  };

  const reportPeriodLabel = () => {
    const m = MONTHS.find((mo) => mo.value === appliedMonth);
    return `${m?.label ?? appliedMonth} ${appliedYear}`;
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const monthName = MONTHS.find((m) => m.value === appliedMonth)?.label || 'Current';
    const filename = `security-fee-report-${monthName.toLowerCase()}-${appliedYear}`;
    const result = await PDFExporter.exportToPDF('report-security-fee', filename, { scale: 1.5 });
    if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    setIsExporting(false);
  };

  const totals = {
    collected: byOperator.reduce((s, r) => s + (r.collected || 0), 0),
    remaining: byOperator.reduce((s, r) => s + (r.remaining || 0), 0),
    eligible: byOperator.reduce((s, r) => s + (r.eligible || 0), 0)
  };

  const handleExportCSV = () => {
    const periodLabel = reportPeriodLabel();
    const generated = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Report', 'Security Fee Collection Report'],
      ['Period', periodLabel],
      ['Generated', generated],
      [],
      ['Total Collected', 'Remaining', 'Eligible Subscribers'],
      [totals.collected, totals.remaining, totals.eligible],
      [],
      ['Operator', 'Eligible Subscribers', 'Collected', 'Remaining'],
      ...byOperator.map((r) => [r.operator, r.eligible, r.collected, r.remaining])
    ];
    const csv = buildCsvRaw(rows);
    const filename = `security-fee-report-${periodLabel.replace(/\s/g, '-')}`;
    downloadCsv(filename, csv);
  };

  const handleExportZip = async (status) => {
    setExportingList(status);
    try {
      const params = { month: appliedMonth, year: appliedYear, status };
      if (operatorFilter !== 'all') params.operator = operatorFilter;
      const { blob, filename } = await securitfeeAPI.exportSecurityFeeSubscribersZip(params);
      downloadBlob(filename, blob);
    } catch (err) {
      alert(`${status === 'collected' ? t('reportSecurityFee.exportCollectedFailed') : t('reportSecurityFee.exportNotCollectedFailed')}: ${err.message || t('common.unknownError')}`);
    } finally {
      setExportingList(null);
    }
  };

  return (
    <div id="report-security-fee" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 print:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="h-8 w-8 text-primary-600" />
                {t('reportSecurityFee.title')}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{t('reportSecurityFee.brand')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('common.reportPeriod')}: <strong>{reportPeriodLabel()}</strong>
                {operatorFilter !== 'all' && <> · {t('common.operator')}: <strong>{operatorFilter}</strong></>}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('common.generated')}: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportCSV}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <FileText className="h-4 w-4" />
                {t('common.exportCSV')}
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Download className="h-4 w-4" />
                {isExporting ? t('common.exporting') : t('common.exportPDF')}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.month')}</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.year')}</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.operator')}</label>
                <select
                  value={operatorFilter}
                  onChange={(e) => setOperatorFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.allOperators')}</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={applyFilters}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Search className="h-4 w-4" />
              {t('common.apply')}
            </button>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.reportPeriod')}: <strong className="text-gray-700">{reportPeriodLabel()}</strong>
            {operatorFilter !== 'all' && (
              <> · {t('common.operator')}: <strong className="text-gray-700">{operatorFilter}</strong></>
            )}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-800">
            <span className="text-red-500 text-lg">{t('reportSecurityFee.failedToLoad')}</span>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4" />
            <p className="text-gray-600">{t('reportSecurityFee.loading')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-green-100 rounded-lg">
                      <Shield className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">{t('reportSecurityFee.totalCollected')}</p>
                      <p className="text-2xl font-bold text-green-700 tabular-nums">{formatCurrency(totals.collected)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleExportZip('collected')}
                    disabled={exportingList === 'collected'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    <FileText className="h-4 w-4" />
                    {exportingList === 'collected' ? t('common.exporting') : t('common.exportZip')}
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-amber-100 rounded-lg">
                      <Shield className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">{t('common.remaining')}</p>
                      <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatCurrency(totals.remaining)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleExportZip('not_collected')}
                    disabled={exportingList === 'not_collected'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    <FileText className="h-4 w-4" />
                    {exportingList === 'not_collected' ? t('common.exporting') : t('common.exportZip')}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t('reportSecurityFee.summaryByOperator')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.collected')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.remaining')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {byOperator.map((row) => (
                      <tr key={row.operator} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium text-gray-900">{row.operator}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(row.collected)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(row.remaining)}</td>
                      </tr>
                    ))}
                    {byOperator.length > 0 && (
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-5 py-3 text-sm text-gray-900">{t('common.total')}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(totals.collected)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(totals.remaining)}</td>
                      </tr>
                    )}
                    {byOperator.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-5 py-10 text-center text-sm text-gray-500">
                          {t('reportSecurityFee.noData')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportSecurityFee;
